/**
 * Admin Progress Tracker - E-Learning
 * Shows all users' progress, quiz scores, watch time, reminders, CSV export
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Download, Send, AlertTriangle, CheckCircle2, Clock,
  Users, FastForward, Award, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ percent, small = false }) {
  const h = small ? 'h-1.5' : 'h-2';
  const color = percent >= 100 ? 'bg-green-500' : percent >= 50 ? 'bg-yellow-500' : percent > 0 ? 'bg-[#3b82f6]' : 'bg-gray-300';
  return (
    <div className={`w-full bg-gray-200 rounded-full ${h}`}>
      <div className={`${h} rounded-full transition-all ${color}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

export default function AdminProgressTracker() {
  const [data, setData] = useState({ users: [], paths: [] });
  const [loading, setLoading] = useState(true);
  const [pathFilter, setPathFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reminderMessage, setReminderMessage] = useState('Please complete your assigned training courses.');
  const [bulkThreshold, setBulkThreshold] = useState(100);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }), []);

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      const url = pathFilter !== 'all'
        ? `${API_BASE}/api/elearning/admin/progress?path_id=${pathFilter}`
        : `${API_BASE}/api/elearning/admin/progress`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders, pathFilter]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/elearning/admin/progress/export`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `elearning_progress_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleSendReminder = async () => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${API_BASE}/api/elearning/admin/send-reminder`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          message: reminderMessage,
        })
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Reminder sent to ${result.to}`);
        setReminderDialogOpen(false);
      }
    } catch {
      toast.error('Failed to send reminder');
    }
  };

  const handleBulkReminder = async () => {
    try {
      const body = {
        message: reminderMessage,
        threshold_pct: bulkThreshold
      };
      if (pathFilter !== 'all') body.path_id = pathFilter;
      const res = await fetch(`${API_BASE}/api/elearning/admin/send-bulk-reminder`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Sent ${result.sent_count} reminders`);
        setBulkReminderOpen(false);
      }
    } catch {
      toast.error('Failed to send reminders');
    }
  };

  // Filter users
  let filteredUsers = data.users;
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filteredUsers = filteredUsers.filter(u =>
      u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }
  if (statusFilter !== 'all') {
    filteredUsers = filteredUsers.filter(u => {
      const totalPct = u.paths.reduce((sum, p) => sum + p.progress_pct, 0) / (u.paths.length || 1);
      if (statusFilter === 'completed') return totalPct >= 100;
      if (statusFilter === 'in_progress') return totalPct > 0 && totalPct < 100;
      if (statusFilter === 'not_started') return totalPct === 0;
      if (statusFilter === 'flagged') return u.fast_forward_flag;
      return true;
    });
  }

  // Summary stats
  const totalUsers = data.users.length;
  const completedAll = data.users.filter(u => u.paths.every(p => p.progress_pct >= 100)).length;
  const inProgress = data.users.filter(u => u.paths.some(p => p.progress_pct > 0) && !u.paths.every(p => p.progress_pct >= 100)).length;
  const notStarted = data.users.filter(u => u.paths.every(p => p.progress_pct === 0)).length;
  const flagged = data.users.filter(u => u.fast_forward_flag).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-progress-tracker">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4 text-center cursor-pointer hover:bg-accent/30" onClick={() => setStatusFilter('all')}>
          <Users className="h-5 w-5 mx-auto mb-1 text-[#3b82f6]" />
          <div className="text-2xl font-bold">{totalUsers}</div>
          <div className="text-xs text-muted-foreground">Total Users</div>
        </Card>
        <Card className="p-4 text-center cursor-pointer hover:bg-accent/30" onClick={() => setStatusFilter('completed')}>
          <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-green-500" />
          <div className="text-2xl font-bold text-green-600">{completedAll}</div>
          <div className="text-xs text-muted-foreground">Completed</div>
        </Card>
        <Card className="p-4 text-center cursor-pointer hover:bg-accent/30" onClick={() => setStatusFilter('in_progress')}>
          <Clock className="h-5 w-5 mx-auto mb-1 text-yellow-500" />
          <div className="text-2xl font-bold text-yellow-600">{inProgress}</div>
          <div className="text-xs text-muted-foreground">In Progress</div>
        </Card>
        <Card className="p-4 text-center cursor-pointer hover:bg-accent/30" onClick={() => setStatusFilter('not_started')}>
          <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />
          <div className="text-2xl font-bold text-red-600">{notStarted}</div>
          <div className="text-xs text-muted-foreground">Not Started</div>
        </Card>
        <Card className="p-4 text-center cursor-pointer hover:bg-accent/30" onClick={() => setStatusFilter('flagged')}>
          <FastForward className="h-5 w-5 mx-auto mb-1 text-orange-500" />
          <div className="text-2xl font-bold text-orange-600">{flagged}</div>
          <div className="text-xs text-muted-foreground">Fast-Forward Flag</div>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              className="pl-9 w-56"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="progress-search"
            />
          </div>
          <Select value={pathFilter} onValueChange={setPathFilter}>
            <SelectTrigger className="w-44" data-testid="path-filter">
              <SelectValue placeholder="All Paths" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Paths</SelectItem>
              {data.paths.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setBulkReminderOpen(true)} data-testid="bulk-reminder-btn">
            <Send className="h-4 w-4 mr-1" /> Send Bulk Reminder
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left p-3 font-medium">User</th>
                <th className="text-left p-3 font-medium">Role</th>
                {data.paths.map(p => (
                  <th key={p.id} className="text-center p-3 font-medium min-w-[120px]">{p.title}</th>
                ))}
                <th className="text-center p-3 font-medium">Watch Time</th>
                <th className="text-center p-3 font-medium">Seeks</th>
                <th className="text-center p-3 font-medium">Quiz Avg</th>
                <th className="text-center p-3 font-medium">Certs</th>
                <th className="text-center p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.user_id} className="border-b hover:bg-accent/20 transition-colors" data-testid={`progress-row-${user.user_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">{user.role?.replace('_', ' ')}</Badge>
                  </td>
                  {user.paths.map(p => (
                    <td key={p.path_id} className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <ProgressBar percent={p.progress_pct} small />
                        <span className="text-xs font-medium">{p.completed_lessons}/{p.total_lessons}</span>
                        <span className="text-[10px] text-muted-foreground">{p.progress_pct}%</span>
                      </div>
                    </td>
                  ))}
                  <td className="p-3 text-center">
                    <span className="text-xs font-medium">{user.total_watch_minutes} min</span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-xs">{user.total_seek_count}</span>
                      {user.fast_forward_flag && (
                        <FastForward className="h-3.5 w-3.5 text-orange-500" />
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {user.quiz_avg_score > 0 ? (
                        <>
                          <Award className="h-3.5 w-3.5 text-[#3b82f6]" />
                          <span className="text-xs font-medium">{user.quiz_avg_score}%</span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">--</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center" data-testid={`certs-${user.user_id}`}>
                    <div className="flex items-center justify-center gap-1">
                      {user.certification_count > 0 ? (
                        <>
                          <Award className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs font-medium">{user.certification_count}</span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">0</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => { setSelectedUser(user); setReminderDialogOpen(true); }}
                      data-testid={`remind-${user.user_id}`}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={data.paths.length + 6} className="p-8 text-center text-muted-foreground">
                    No users match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Single Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Send Reminder</DialogTitle>
            <DialogDescription>
              Send a training reminder to {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Message</Label>
              <textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                className="mt-1 w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendReminder} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="send-reminder-confirm">
              <Send className="h-4 w-4 mr-2" /> Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Reminder Dialog */}
      <Dialog open={bulkReminderOpen} onOpenChange={setBulkReminderOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Send Bulk Reminder</DialogTitle>
            <DialogDescription>
              Send reminders to users below a completion threshold
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Completion Threshold (%)</Label>
              <p className="text-xs text-muted-foreground mb-2">Users below this % will receive a reminder</p>
              <Input
                type="number"
                value={bulkThreshold}
                onChange={(e) => setBulkThreshold(parseInt(e.target.value) || 0)}
                min={0}
                max={100}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message</Label>
              <textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                className="mt-1 w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBulkReminderOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkReminder} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="send-bulk-confirm">
              <Send className="h-4 w-4 mr-2" /> Send to All
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
